import React, { useState, useCallback, useEffect } from "react";
import "./organization-access-list-overview.scss";

import {
  GSForm,
  GSSidePanelPage,
  GSInput,
  GSEmptyList,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faCircleArrowDown,
  faCircleArrowUp,
  faPen,
  faPlus,
  faSearch,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";
import { selectCurrentOrganization } from "../../reducers/organizationSlice";

import { accessListOverviewForm } from "../../forms/AccessListForm";
import {
  selectAccessListNotifications,
  selectCurrentList,
  getOrganizationAccessList,
  createAccessList,
  clearNotifications,
  setSelectedList,
  getOrganizationAccessListCodes,
  selectCurrentListCodes,
  selectLoading,
  downloadOrganizationAccessList,
} from "../../reducers/accessSlice";

const OrganizationAccessListOverview = () => {
  const [isValid, setIsValid] = useState(false);

  const [list, setList] = useState({});
  const [name, setName] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const matchEdit = useMatch("/:id/access/:accessListID/*");

  const [memberFilter, setMemberFilter] = useState({
    id: matchEdit?.params?.accessListID,
    searchText: "",
    page: "1",
    perPage: "1000",
  });

  const orgNotifications = useSelector(selectAccessListNotifications);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const currentList = useSelector(selectCurrentList);
  const currentListCodes = useSelector(selectCurrentListCodes);
  const loading = useSelector(selectLoading);

  const loadList = useCallback(() => {
    if (matchEdit?.params?.accessListID !== "add") {
      dispatch(getOrganizationAccessList(matchEdit?.params?.accessListID));
    } else {
      const newList = {
        name: "",
        description: "",
        status: "active",
      };
      setList(newList);
    }
  }, [dispatch, matchEdit?.params]);

  useEffect(() => {
    loadList();
    return () => {
      dispatch(setSelectedList({}));
    };
  }, [loadList, dispatch]);

  useEffect(() => {
    const { id, accessListID } = matchEdit?.params;
    if (currentList?.id) {
      if (currentList?.id !== accessListID) {
        navigate(`/${id}/access/${currentList.id}/edit`);
      } else {
        setList(currentList);
      }
    } else {
      const newList = {
        name: "",
        description: "",
        status: "active",
      };
      setList(newList);
    }
  }, [currentList, matchEdit?.params, navigate]);

  useEffect(() => {
    dispatch(getOrganizationAccessListCodes(memberFilter));
  }, [memberFilter, dispatch]);

  //form context
  let context = {};

  context.validationFailed = () => {
    if (isValid) {
      setIsValid(false);
    }
  };

  context.updateList = (value, property) => {
    setIsValid(true);
    let group = { ...list };
    group[property] = value;
    setList(group);
  };

  context.isEditable = () => {
    return matchEdit?.params?.accessListID === "add";
  };

  context.getFilteredList = () => {
    return currentListCodes;
  };

  context.createCode = () => {
    navigate("code/add");
  };

  context.selectCode = (code) => {
    navigate(`code/${code.id}`);
  };

  context.membersAreLoading = () => {
    return loading?.includes?.("getOrganizationAccessListCodes");
  };

  context.getEmptySearchList = () => {
    return name !== "" ? (
      <GSEmptyList
        title="We couldn’t find who you’re looking for…"
        detail="Try changing the search term or add a new code."
        actions={[
          {
            title: "Clear Search",
            type: "light-grey",
            onClick: clearText,
          },
          {
            title: "Add Code",
            type: "black",
            buttonIcon: faPlus,
            onClick: context.createCode,
          },
        ]}
      />
    ) : (
      <GSEmptyList
        title={`${currentList?.name} Codes`}
        detail="Add codes to limit event registration and reserve packages for specific registrants."
        actions={[
          {
            title: "Add Code",
            type: "black",
            buttonIcon: faPlus,
            onClick: context.createCode,
          },
        ]}
      />
    );
  };

  const addList = () => {
    setIsValid(false);
    let create = { ...list };
    create.organization = currentOrganization;
    dispatch(createAccessList(create));
  };

  const leftNavigation = () => {
    dispatch(clearNotifications());
    const { id } = matchEdit?.params;
    navigate(`/${id}/access`);
  };

  const exportList = () => {
    dispatch(downloadOrganizationAccessList(matchEdit.params.accessListID));
  };

  const uploadList = () => {
    navigate("upload");
  };

  const getActions = () => {
    let actions = [];

    if (context?.isEditable?.()) {
      actions.unshift?.({
        name: "Create List",
        isDisabled: !isValid,
        action: addList,
        type: "black",
      });
    } else {
      actions?.push?.({
        name: "Upload codes",
        action: uploadList,
        buttonIcon: faCircleArrowUp,
        type: "light-grey",
      });
      actions?.push?.({
        name: "Download List",
        action: exportList,
        buttonIcon: faCircleArrowDown,
        type: "light-grey",
      });
    }
    return actions;
  };

  const getNavigation = () => {
    return {
      title: "Access List Overview",
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const hasNotifications = (notifications) => {
    return notifications && notifications.length > 0;
  };

  const getBanner = () => {
    const slideOutNotifications = orgNotifications;
    if (hasNotifications(slideOutNotifications)) {
      return {
        notifications: slideOutNotifications.map((n) => ({
          ...n,
          pageActions: getBannerActions(),
        })),
        timeoutAction: timeoutAction,
        timeout: 10000,
      };
    }
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const getBannerActions = () => {
    return [
      {
        id: 1,
        title: "View Access Lists",
        size: "secondary",
        actionClick: leftNavigation,
      },
    ];
  };

  const getActiveAction = () => {
    return [
      {
        type: "light-grey ",
        actionIcon: faPen,
        actionClick: () => {
          navigate("edit");
        },
      },
    ];
  };

  const getFormActionBar = () => {
    return {
      formTitle: currentList.name ,
      formActions: getActiveAction(),
    };
  };

  const getContent = () => {
    return (
      <GSForm
        {...getFormActionBar()}
        formSections={accessListOverviewForm(context, list)}
      />
    );
  };
  const getDrawer = () => {
    return {
      actions: getActions(),
    };
  };
  const clearText = () => {
    setName("");
    setMemberFilter({ ...memberFilter, searchText: "" });
  };
  const searchChanged = (e) => {
    setName(e.target.value);
  };
  const submitSearch = () => {
    setMemberFilter({ ...memberFilter, searchText: name });
  };
  context.memberSearch = () => {
    return (
      <div className="search">
        <GSInput
          textValue={name}
          rightIconClick={clearText}
          rightIcon={faTimesCircle}
          leftIcon={faSearch}
          onChange={searchChanged}
          onSubmit={submitSearch}
          placeholder="Search by Code, Name, or Email Address…"
        ></GSInput>
      </div>
    );
  };
  return (
    <organization-access-list-overview>
      <GSSidePanelPage
        header={getNavigation()}
        banner={getBanner()}
        content={getContent()}
        drawer={getDrawer()}
      />
    </organization-access-list-overview>
  );
};

export default OrganizationAccessListOverview;
